<template>
  <div>
    <b-card>
      <div class="manifest-list-header mb-2">
        <h4 class="mb-0">Suivi sous-traitance</h4>
        <div class="manifest-list-actions">
          <div class="manifest-date-filter">
            <label
              for="manifest-created-date-filter"
              class="manifest-date-label"
            >
              <feather-icon icon="CalendarIcon" size="15" />
              Date de création
            </label>
            <div class="manifest-date-control">
              <b-form-datepicker
                id="manifest-created-date-filter"
                v-model="createdAtDateFilter"
                locale="fr"
                placeholder="Choisir une date"
                size="sm"
                today-button
                reset-button
                close-button
                @input="applyFilters"
              />
              <b-button
                v-if="createdAtDateFilter"
                variant="outline-secondary"
                size="sm"
                class="manifest-clear-filter-btn"
                @click="clearDateFilter"
              >
                Effacer
              </b-button>
            </div>
          </div>
          <b-button
            v-if="canCreate"
            variant="primary"
            :to="{ name: 'manifest-create' }"
          >
            <feather-icon icon="PlusIcon" class="mr-1" />
            Créer un bon
          </b-button>
        </div>
      </div>

      <vue-good-table
        :columns="columns"
        :rows="manifestRows"
        :is-loading="isLoading"
        :pagination-options="paginationOptions"
        :search-options="searchOptions"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'created_by_label'">
            {{ props.row.created_by_label }}
            <br>
            <small class="text-muted">{{ props.row.created_by_role }}</small>
          </span>
          <span v-else-if="props.column.field === 'status_display'">
            <b-badge :variant="statusVariant(props.row.status)" pill>
              {{ statusLabel(props.row.status) }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'tracking_counts'">
            {{ props.row.treated_count || 0 }} / {{ props.row.orders_count || 0 }}
            <br>
            <small class="text-muted">Restant : {{ props.row.remaining_count || 0 }}</small>
          </span>
          <span v-else-if="props.column.field === 'actions'">
            <b-button
              size="sm"
              variant="outline-primary"
              :to="{ name: 'manifest-detail', params: { id: props.row.id } }"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
        </template>
      </vue-good-table>
    </b-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { VueGoodTable } from 'vue-good-table'

export default {
  name: 'ManifestsPickupIndex',
  components: { VueGoodTable },
  data() {
    return {
      createdAtDateFilter: '',
    }
  },
  computed: {
    ...mapGetters('manifestsPickup', ['manifests', 'isLoading']),
    ...mapGetters(['userRole']),
    canCreate() {
      return ['admin', 'superadmin', 'manager'].includes(this.userRole)
    },
    manifestRows() {
      return this.manifests.map(manifest => ({
        ...manifest,
        outsourcing_company_label: manifest.outsourcing_company?.display_name || manifest.outsourcing_company?.name || '-',
        created_by_label: manifest.created_by?.username || '-',
        created_by_role: manifest.created_by?.role || '-',
        note_label: manifest.note || '-',
        created_at_label: this.formatDate(manifest.created_at),
        tracking_counts: `${manifest.treated_count || 0}/${manifest.orders_count || 0}`,
      }))
    },
    columns() {
      return [
        { label: '#', field: 'id', type: 'number' },
        { label: 'Société de sous-traitance', field: 'outsourcing_company_label' },
        { label: 'Statut', field: 'status_display' },
        { label: 'Créé par', field: 'created_by_label' },
        { label: 'Traitement', field: 'tracking_counts', sortable: false },
        { label: 'Note', field: 'note_label' },
        { label: 'Créé le', field: 'created_at_label' },
        { label: 'Actions', field: 'actions', sortable: false },
      ]
    },
    paginationOptions() {
      return {
        enabled: true,
        mode: 'pages',
        perPage: 20,
      }
    },
    searchOptions() {
      return {
        enabled: true,
        placeholder: 'Rechercher un bon',
      }
    },
  },
  created() {
    this.applyFilters()
  },
  methods: {
    ...mapActions('manifestsPickup', ['fetchManifests']),
    applyFilters() {
      const params = {}
      if (this.createdAtDateFilter) {
        params.created_at_date = this.createdAtDateFilter
      }
      this.fetchManifests(params)
    },
    clearDateFilter() {
      this.createdAtDateFilter = ''
      this.applyFilters()
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString('fr-TN')
    },
    statusVariant(statusValue) {
      const variants = {
        ouvert: 'light-warning',
        en_cours: 'light-warning',
        termine: 'light-success',
        annule: 'light-danger',
      }
      return variants[statusValue] || 'light-secondary'
    },
    statusLabel(statusValue) {
      if (statusValue === 'termine') return 'Clôturé'
      if (statusValue === 'annule') return 'Annulé'
      return 'Non clôturé'
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>

<style scoped>
.manifest-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.manifest-list-actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.manifest-date-filter {
  min-width: 245px;
  padding: 0.65rem 0.75rem;
  border: 1px solid #d8e0ea;
  border-radius: 0.65rem;
  background: #f8fafc;
}

.manifest-date-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #526071;
}

.manifest-date-control {
  display: flex;
  gap: 0.5rem;
}

.manifest-date-control ::v-deep .b-form-datepicker {
  min-width: 190px;
}

.manifest-clear-filter-btn {
  height: 31px;
}

@media (max-width: 767.98px) {
  .manifest-list-header,
  .manifest-list-actions,
  .manifest-date-control {
    align-items: stretch;
    flex-direction: column;
  }

  .manifest-list-actions,
  .manifest-date-filter {
    width: 100%;
  }
}
</style>
